import { useLayoutEffect, useRef, useState } from "react";
import {
  TOOLTIP_LABEL,
  TOOLTIP_SUBTITLE,
  TOOLTIP_SURFACE,
  TOOLTIP_TITLE
} from "../ChartTooltip";

export type JourneyTooltipContent = {
  title: string;
  /** The headline count, rendered like the total on the bar charts. */
  subtitle?: string;
  rows: string[];
};

const EDGE_PADDING = 8;
const CURSOR_OFFSET = 14;

/**
 * Dark hover tooltip for the Sankey.
 *
 * Positioned fixed and nudged back inside the viewport after measuring itself,
 * so it stays readable near the right edge and inside the chart's own
 * horizontal scroller — where an absolutely positioned tooltip would be
 * clipped.
 */
export const JourneyTooltip = ({
  content
}: {
  content: (JourneyTooltipContent & { x: number; y: number }) | null;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || !content) return;

    const rect = element.getBoundingClientRect();
    let left = content.x + CURSOR_OFFSET;
    let top = content.y - rect.height - 12;
    if (left + rect.width > window.innerWidth - EDGE_PADDING)
      left = content.x - CURSOR_OFFSET - rect.width;
    if (top < EDGE_PADDING) top = content.y + 16;

    setPosition({ left: Math.max(EDGE_PADDING, left), top });
  }, [content]);

  if (!content) return null;

  return (
    <div
      ref={ref}
      role="tooltip"
      className={`fixed z-50 ${TOOLTIP_SURFACE}`}
      style={position}
    >
      <p className={TOOLTIP_TITLE}>{content.title}</p>
      {content.subtitle ? (
        <p className={TOOLTIP_SUBTITLE}>{content.subtitle}</p>
      ) : null}
      {content.rows.map((row) => (
        <p key={row} className={TOOLTIP_LABEL}>
          {row}
        </p>
      ))}
    </div>
  );
};
